import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: `${API}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Try to refresh token
      try {
        const refreshResponse = await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
        const { access_token } = refreshResponse.data;
        localStorage.setItem('access_token', access_token);
        
        // Retry original request
        error.config.headers.Authorization = `Bearer ${access_token}`;
        return axios(error.config);
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Format API error for display
export const formatApiError = (error) => {
  const detail = error.response?.data?.detail;
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

// Activities API
export const activitiesApi = {
  getAll: (params) => api.get('/activities', { params }),
  getById: (id) => api.get(`/activities/${id}`),
  create: (data) => api.post('/activities', data),
  update: (id, data) => api.put(`/activities/${id}`, data),
  delete: (id) => api.delete(`/activities/${id}`),
  reuse: (id, data) => api.post(`/activities/${id}/reuse`, data),
  register: (id, data) => api.post(`/activities/${id}/register`, data),
  cancelRegistration: (id) => api.delete(`/activities/${id}/register`),
  getRegistrations: (id) => api.get(`/activities/${id}/registrations`),
  toggleFavorite: (id) => api.post(`/activities/${id}/favorite`),
  getFavorites: () => api.get('/favorites'),
  getFavoriteIds: () => api.get('/favorites/ids'),
  trackView: (id) => api.post(`/activities/${id}/view`),
  waitlistNewDate: (id, data) => api.post(`/activities/${id}/waitlist/new-date`, data),
  waitlistNoNewDate: (id) => api.post(`/activities/${id}/waitlist/no-new-date`, {}),
  cancelActivity: (id) => api.post(`/activities/${id}/cancel`),
  getRefunds: (id) => api.get(`/activities/${id}/refunds`),
  validateCoupon: (id, code) => api.post(`/activities/${id}/validate-coupon`, { coupon_code: code }),
};

// Registrations API
export const registrationsApi = {
  getMine: () => api.get('/my-registrations'),
  confirmPayment: (registrationId) => api.put(`/registrations/${registrationId}/confirm-payment`),
  paymentNotFound: (registrationId) => api.put(`/registrations/${registrationId}/payment-not-found`),
  sendPaymentReminder: (registrationId) => api.post(`/registrations/${registrationId}/payment-reminder`),
  markRefundComplete: (registrationId) => api.post(`/registrations/${registrationId}/refund-complete`),
};

// Messaging API
export const messagingApi = {
  getConversations: () => api.get('/conversations'),
  getMessages: (conversationId) => api.get(`/conversations/${conversationId}/messages`),
  sendMessage: (data) => api.post('/messages', data),
};

// Users API
export const usersApi = {
  getProfile: (id) => api.get(`/users/${id}`),
  updateProfile: (data) => api.put('/users/profile', data),
  updateRole: (role) => api.put('/users/role', { role }),
  getActivities: (id, includePast = false) => api.get(`/users/${id}/activities`, { params: { include_past: includePast } }),
  follow: (id) => api.post(`/users/${id}/follow`),
  getReviews: (id, params) => api.get(`/users/${id}/reviews`, { params }),
};

// Creator Gallery API
export const galleryApi = {
  get: (identifier) => api.get(`/creator/${identifier}/gallery`),
  add: (data) => api.post('/creator/gallery', data),
  remove: (photoId) => api.delete(`/creator/gallery/${photoId}`),
};

// Auth API
export const authApi = {
  me: () => api.get('/auth/me'),
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  googleSession: (sessionId) => api.post('/auth/google/session', { session_id: sessionId }),
};

// Categories API
export const categoriesApi = {
  getAll: () => api.get('/categories'),
};

// Coupons API
export const couponsApi = {
  create: (data) => api.post('/coupons', data),
  getMine: () => api.get('/coupons'),
  delete: (id) => api.delete(`/coupons/${id}`),
};

// Reviews API
export const reviewsApi = {
  create: (data) => api.post('/reviews', data),
};

// Notifications API
export const notificationsApi = {
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
};

// Upload API
export const uploadApi = {
  uploadFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  upload: async (formData) => {
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Creator Dashboard API
export const creatorApi = {
  getDashboard: () => api.get('/creator/dashboard'),
  getGeneralStats: (params) => api.get('/creator/general-stats', { params }),
  getPaymentMethods: () => api.get('/creator/payment-methods'),
  updatePaymentMethods: (data) => api.put('/creator/payment-methods', data),
  getAnalytics: (params) => api.get('/creator/analytics', { params }),
};

// Recommendations API
export const recommendationsApi = {
  getAll: () => api.get('/recommendations'),
  getFeatured: () => api.get('/recommendations/featured'),
};

// Admin Recommendation Themes API
export const adminThemesApi = {
  getAll: () => api.get('/admin/recommendation-themes'),
  create: (data) => api.post('/admin/recommendation-themes', data),
  update: (id, data) => api.put(`/admin/recommendation-themes/${id}`, data),
  delete: (id) => api.delete(`/admin/recommendation-themes/${id}`),
};

export default api;
